/* ==========================================================================
   global.js :: Ordin Daylight Clone (Phase C) - site-wide behaviors

   1. Header scroll state (.is-scrolled on [data-site-header], on >80px off <20px;
      styles in motion.css).
   2. Mobile nav overlay: [data-nav-toggle] open/close, aria sync, Escape,
      basic focus trap, scroll lock, Ordin.lenis stop/start (null-safe).
   3. HOME feature video lazy init: data-src -> src on IO enter + play,
      pause on exit. The PDP gallery video ([data-gallery-video]) belongs
      to Phase D (pdp.js) - do not touch it here.

   Cart/PDP commerce behaviors (drawer, ATC, qty, gallery, quotes) are
   Phase D property (cart.js / pdp.js).
   ========================================================================== */
(function () {
  'use strict';

  var designMode = !!(window.Shopify && window.Shopify.designMode);
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ----- 1. header scroll state -------------------------------------------- */

  var header = document.querySelector('[data-site-header]');
  if (header) {
    /* 2026-10-27 (founder: header flickers at the very top on iPhone): a single
       24px threshold toggled the class (and its 0.25s shadow fade) on every
       crossing, so iOS momentum settling / rubber-band near the top blinked it.
       Hysteresis (on > 80px, off < 20px) + clamp negative bounce scrollY to 0.
       The class only changes box-shadow, never height, so nothing feeds back
       into the scroll position. */
    var ticking = false;
    var scrolled = null;
    var updateHeader = function () {
      ticking = false;
      var y = Math.max(0, window.scrollY || window.pageYOffset || 0);
      var next = scrolled ? y > 20 : y > 80;
      if (next === scrolled) return;
      scrolled = next;
      header.classList.toggle('is-scrolled', next);
    };
    window.addEventListener('scroll', function () {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(updateHeader);
      }
    }, { passive: true });
    updateHeader();
  }

  /* ----- 2. mobile nav overlay ---------------------------------------------- */

  var nav = document.querySelector('[data-mobile-nav]');
  if (nav) {
    var burger = document.querySelector('.site-header__burger[data-nav-toggle]');
    var toggles = document.querySelectorAll('[data-nav-toggle]');
    var lastFocus = null;

    var isOpen = function () { return nav.classList.contains('is-open'); };

    var setLenis = function (stop) {
      var lenis = window.Ordin && window.Ordin.lenis;
      if (!lenis) return;
      if (stop) { lenis.stop(); } else { lenis.start(); }
    };

    var openNav = function () {
      lastFocus = document.activeElement;
      nav.classList.add('is-open');
      nav.setAttribute('aria-hidden', 'false');
      if (burger) burger.setAttribute('aria-expanded', 'true');
      /* 2026-10-27: shared events lock (assets/scroll-lock.js), no overflow toggle */
      if (window.OrdinScrollLock) window.OrdinScrollLock.lock('mobile-nav', nav);
      var first = nav.querySelector('a[href], button, input');
      if (first) first.focus();
    };

    var closeNav = function () {
      nav.classList.remove('is-open');
      nav.setAttribute('aria-hidden', 'true');
      if (burger) burger.setAttribute('aria-expanded', 'false');
      if (window.OrdinScrollLock) window.OrdinScrollLock.unlock('mobile-nav');
      if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
      lastFocus = null;
    };

    toggles.forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (isOpen()) { closeNav(); } else { openNav(); }
      });
    });

    /* Following a link should close the overlay (same-page anchors too). */
    nav.querySelectorAll('.mobile-nav__links a, .mobile-nav__cta').forEach(function (a) {
      a.addEventListener('click', closeNav);
    });

    document.addEventListener('keydown', function (e) {
      if (!isOpen()) return;
      if (e.key === 'Escape') {
        closeNav();
        return;
      }
      if (e.key !== 'Tab') return;
      /* basic focus trap */
      var focusables = nav.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])'
      );
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });
  }

  /* ----- 2b. newsletter submit feedback (Phase U) ----------------------------
     The forms are native Shopify page-POSTs; between click and navigation
     there is no feedback and double-submits are possible. On submit
     (form already dispatching, so disabling cannot block it) the button
     locks and reads "Sending".

     2026-10-11 (founder-approved signup repair): the lock now recovers.
     Shopify's storefront-forms-hCaptcha binds every customer form: it
     preventDefaults the submit, runs hcaptcha.execute(), and only then calls
     the native form.submit(). When execute() never settles or rejects (script
     blocked by an ad blocker or network, a challenge the visitor closes) it
     only logs "form submit failed" -- nothing reaches the page, so the old
     lock sat on "Sending" forever and the visitor could not try again.
     Now: if the page has not started navigating after NL_TIMEOUT_MS, the
     button is restored and a visible, announced notice asks for a retry.
     While an hCaptcha challenge is on screen the visitor is still working,
     so the check waits instead of interrupting. Nothing here submits on the
     visitor's behalf and nothing claims success -- success is still only the
     server's ?customer_posted=true render. pageshow also clears a lock that
     the back/forward cache restored. */

  var NL_TIMEOUT_MS = 15000;
  var NL_RECHECK_MS = 5000;
  var nlNavigating = false;
  window.addEventListener('beforeunload', function () { nlNavigating = true; });

  function nlChallengeOpen() {
    var frames = document.querySelectorAll('iframe[src*="hcaptcha"][src*="frame=challenge"]');
    for (var i = 0; i < frames.length; i++) {
      if (window.getComputedStyle(frames[i]).visibility === 'visible') return true;
    }
    return false;
  }

  function nlUnlock(form) {
    var btn = form.querySelector('button[type="submit"]');
    if (form._nlTimer) { window.clearTimeout(form._nlTimer); form._nlTimer = null; }
    if (!btn || !btn.hasAttribute('data-nl-label')) return;
    btn.innerHTML = btn.getAttribute('data-nl-label');
    btn.removeAttribute('data-nl-label');
    btn.removeAttribute('aria-busy');
    btn.disabled = false;
  }

  function nlNotice(form) {
    var note = form.querySelector('[data-nl-timeout]');
    if (!note) {
      note = document.createElement('p');
      note.className = 'newsletter-form__error' + (form.closest('.email-popup') ? ' email-popup__error' : '');
      note.setAttribute('role', 'alert');
      note.setAttribute('data-nl-timeout', '');
      var anchor = form.querySelector('.email-popup__field') || form.querySelector('label[for]') || form.querySelector('input[type="email"]');
      form.insertBefore(note, anchor && anchor.parentNode === form ? anchor : form.firstChild);
    }
    form._nlFails = (form._nlFails || 0) + 1;
    note.textContent = form._nlFails > 1
      ? 'Still not through. Refresh the page and try once more.'
      : 'That didn’t go through. Please try again.';
  }

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!(form instanceof Element) || !form.classList.contains('newsletter-form')) return;
    var btn = form.querySelector('button[type="submit"]');
    if (!btn || btn.disabled) return;
    btn.setAttribute('data-nl-label', btn.innerHTML);
    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');
    btn.textContent = 'Sending';
    var check = function () {
      if (nlNavigating) return;
      if (nlChallengeOpen()) { form._nlTimer = window.setTimeout(check, NL_RECHECK_MS); return; }
      nlUnlock(form);
      nlNotice(form);
      var input = form.querySelector('input[type="email"]');
      if (input) input.focus();
    };
    form._nlTimer = window.setTimeout(check, NL_TIMEOUT_MS);
  });

  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    nlNavigating = false;
    document.querySelectorAll('form.newsletter-form').forEach(nlUnlock);
  });

  /* ----- 3. home feature video (lazy init + IO play/pause) ------------------ */

  var video = document.querySelector('[data-section="home-feature-video"] video[data-src]');
  if (video && 'IntersectionObserver' in window && !designMode && !reduced) {
    var vio = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          if (!video.getAttribute('src')) {
            video.src = video.getAttribute('data-src');
            video.load();
          }
          var p = video.play();
          if (p && typeof p.catch === 'function') p.catch(function () { /* autoplay denied: poster stays */ });
        } else if (video.getAttribute('src')) {
          video.pause();
        }
      });
    }, { threshold: 0.25 });
    vio.observe(video);
  }
})();

/* ==========================================================================
   ===== STORE-EXPANSION HEADER BAND (2026-08-06) =====

   Behaviour for the reworked header. Everything above is the pre-expansion
   theme and is untouched. This IIFE is self-contained, idempotent (it stamps
   data-sx-init on every root it wires), leaks no globals and feature-detects
   before using fetch / Intl / matchMedia / String.fromCodePoint.

   8 units (E-G added 2026-08-08, Batch 3 / Phase B -- collection + search;
            H added 2026-08-27, T14 -- bundles-page mobile density):
     A. nav mega-panels    [data-nav-root] triggers <-> [data-nav-panel]
     B. search overlay     [data-search-open] -> [data-search-overlay],
                           250ms-debounced /search/suggest.json
     C. country selector   [data-country-select] (progressive enhancement over
                           the native <select> base layer)
     D. mobile accordions  [data-mnav-acc] <-> [data-mnav-panel]
                           (EXCLUSIVE-OPEN since 2026-08-27)
     E. card quick-add     [data-quick-add] -> OrdinCart.add -> drawer
     F. category chips     [data-filter-chips] -> [data-filter-grid]
     G. sort auto-submit   [data-sort-select] -> real ?sort_by
     H. bundles prose      [data-sx-prose-toggle] <-> [data-sx-prose], <760px
   ========================================================================== */
(function () {
  'use strict';

  var doc = document;
  var toArray = function (list) { return Array.prototype.slice.call(list || []); };
  var mq = function (q) {
    return (window.matchMedia && window.matchMedia(q).matches) || false;
  };
  var isDesktop = function () { return mq('(min-width: 900px)'); };
  var canHover = function () { return mq('(hover: hover) and (pointer: fine)'); };

  var esc = function (s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  };

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled])';

  var header = doc.querySelector('[data-site-header]');

  /* ----- A. nav mega-panels ------------------------------------------------ */

  var closeNavPanel = function () {};

  (function navPanels() {
    if (!header || header.getAttribute('data-sx-nav') === '1') return;
    var navRoot = header.querySelector('[data-nav-root]');
    var wrap = header.querySelector('[data-nav-panels]');
    if (!navRoot || !wrap) return;
    header.setAttribute('data-sx-nav', '1');

    var triggers = toArray(navRoot.querySelectorAll('[data-nav-trigger]'));
    var panels = {};
    toArray(wrap.querySelectorAll('[data-nav-panel]')).forEach(function (p) {
      panels[p.getAttribute('data-nav-panel')] = p;
    });

    var openKey = null;
    var timer = null;
    /* Escape closes the panel and returns focus to its trigger -- and that
       programmatic .focus() is keyboard-context, so :focus-visible is true and
       the focus handler immediately re-opened the panel Escape just shut.
       Verified 2026-08-06 via CDP: Escape left anyOpen=true. This window
       suppresses the focus-open path for one tick after a deliberate close. */
    var suppressUntil = 0;

    var close = function () {
      if (timer) { clearTimeout(timer); timer = null; }
      if (!openKey) return;
      var p = panels[openKey];
      if (p) { p.classList.remove('is-open'); p.hidden = true; }
      var t = navRoot.querySelector('[data-nav-trigger="' + openKey + '"]');
      if (t) t.setAttribute('aria-expanded', 'false');
      openKey = null;
    };
    closeNavPanel = close;

    var open = function (key) {
      if (!isDesktop() || openKey === key) return;
      var p = panels[key];
      if (!p) return;
      close();
      var trig = navRoot.querySelector('[data-nav-trigger="' + key + '"]');
      /* narrow EXPLORE card: anchor it under its own tab, clamped to viewport */
      var menu = p.querySelector('.nav-panel__inner--menu');
      if (trig && menu) {
        var r = trig.getBoundingClientRect();
        var w = 236;
        var left = Math.min(Math.max(r.left + (r.width / 2) - (w / 2), 16), Math.max(16, window.innerWidth - w - 16));
        p.style.setProperty('--nav-anchor', Math.round(left) + 'px');
      }
      p.hidden = false;
      p.classList.add('is-open');
      if (trig) trig.setAttribute('aria-expanded', 'true');
      openKey = key;
    };

    var scheduleOpen = function (key) {
      if (timer) clearTimeout(timer);
      timer = setTimeout(function () { open(key); }, 80); /* hover intent */
    };
    var scheduleClose = function () {
      if (timer) clearTimeout(timer);
      timer = setTimeout(close, 140);
    };

    triggers.forEach(function (t) {
      var key = t.getAttribute('data-nav-trigger');

      t.addEventListener('mouseenter', function () {
        if (canHover()) scheduleOpen(key);
      });

      /* keyboard: focus opens. A mouse click also focuses, so gate on
         :focus-visible where supported -- on touch the tab must navigate,
         never spring a panel open under the thumb. */
      t.addEventListener('focus', function () {
        if (Date.now() < suppressUntil) return;
        if (!canHover() && t.tagName !== 'BUTTON') return;
        var vis = true;
        try { vis = t.matches(':focus-visible'); } catch (e) { vis = true; }
        if (vis) open(key);
      });

      /* EXPLORE has no destination, so it is the only click-toggle. */
      if (t.tagName === 'BUTTON') {
        t.addEventListener('click', function (e) {
          e.preventDefault();
          if (openKey === key) { close(); } else { if (timer) clearTimeout(timer); openKey = null; open(key); }
        });
      }

      t.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          open(key);
          var p = panels[key];
          var first = p && p.querySelector(FOCUSABLE);
          if (first) first.focus();
        }
      });
    });

    /* panelsWrap is a DOM child of <header>, so moving the pointer into an
       open panel does NOT fire header's mouseleave (mouseleave is DOM-scoped,
       not geometric). One listener on the header therefore covers both. */
    header.addEventListener('mouseenter', function () { if (timer) clearTimeout(timer); });
    header.addEventListener('mouseleave', function () { if (canHover()) scheduleClose(); });

    doc.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' || !openKey) return;
      var k = openKey;
      close();
      suppressUntil = Date.now() + 400;
      var t = navRoot.querySelector('[data-nav-trigger="' + k + '"]');
      if (t) t.focus();
    });

    /* Tab out of the last item in a panel returns to the trigger and closes */
    wrap.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab' || e.shiftKey || !openKey) return;
      var p = panels[openKey];
      if (!p) return;
      var items = toArray(p.querySelectorAll(FOCUSABLE));
      if (!items.length || doc.activeElement !== items[items.length - 1]) return;
      e.preventDefault();
      var t = navRoot.querySelector('[data-nav-trigger="' + openKey + '"]');
      close();
      suppressUntil = Date.now() + 400;
      if (t) t.focus();
    });

    doc.addEventListener('click', function (e) {
      if (!openKey) return;
      if (header.contains(e.target)) return;
      close();
    });

    /* D9 (2026-08-06): the cart drawer is a full-height overlay with its own
       backdrop, but the mega-panel is absolutely positioned under the sticky
       header and kept rendering behind it -- close the drawer and the panel
       is still sitting there. cart.js binds [data-cart-open] delegated on
       document, so this runs in the CAPTURE phase to be certain the panel is
       gone before the drawer paints. Not folded into the focusin rule below:
       on macOS Safari clicking a <button> does not focus it, so the focus
       path alone would miss this on the browser most of the traffic uses. */
    doc.addEventListener('click', function (e) {
      if (!openKey) return;
      var el = e.target;
      if (!el || !el.closest) return;
      if (el.closest('[data-cart-open]')) close();
    }, true);

    /* D1 (2026-08-06): the old rule was `close unless the focus landed inside
       <header>`, and the panels live INSIDE <header> -- so tabbing off a tab
       into the header's own right cluster (country -> account -> search ->
       cart) never satisfied it and the panel stayed open and rendering for
       the whole walk. The correct test is narrower: keep the panel only while
       focus is inside THAT panel or on its own trigger; anything else closes.
       Ordering is safe -- the spec dispatches focus before focusin, so when
       the walk lands on the NEXT tab that tab's focus handler has already
       re-pointed openKey at itself and this sees a matching trigger.
       Hover is untouched: no focus event, no close. */
    doc.addEventListener('focusin', function (e) {
      if (!openKey) return;
      var el = e.target;
      var p = panels[openKey];
      if (p && el && p.contains(el)) return;
      var t = navRoot.querySelector('[data-nav-trigger="' + openKey + '"]');
      if (t && el && (t === el || t.contains(el))) return;
      close();
    });

    window.addEventListener('resize', function () { if (!isDesktop()) close(); }, { passive: true });
  }());

  /* ----- B. search overlay + predictive results ---------------------------- */

  (function siteSearch() {
    var overlay = doc.querySelector('[data-search-overlay]');
    var opener = doc.querySelector('[data-search-open]');
    if (!overlay || !opener || overlay.getAttribute('data-sx-init') === '1') return;
    if (typeof window.fetch !== 'function') return; /* no-JS/old-JS: the <a href> stays a real link */
    overlay.setAttribute('data-sx-init', '1');

    var input = overlay.querySelector('[data-search-input]');
    var results = overlay.querySelector('[data-search-results]');
    var closeBtn = overlay.querySelector('[data-search-close]');
    var form = overlay.querySelector('form');
    var base = overlay.getAttribute('data-predictive-url') || '/search/suggest.json';
    var currency = overlay.getAttribute('data-currency') || '';
    var locale = overlay.getAttribute('data-locale') || 'en';
    var debounce = null;
    var seq = 0;

    var money = function (raw) {
      var n = parseFloat(raw);
      if (isNaN(n)) return '';
      /* narrowSymbol keeps AUD as "$100.00" rather than "A$100.00", matching
         what the Liquid `money` filter renders in the nav panels and on the
         /search grid -- two different price strings for the same product on
         the same screen reads like a bug. */
      if (window.Intl && Intl.NumberFormat && currency) {
        try { return new Intl.NumberFormat(locale, { style: 'currency', currency: currency, currencyDisplay: 'narrowSymbol' }).format(n); }
        catch (e) { /* narrowSymbol unsupported (older Safari): fall back */ }
        try { return new Intl.NumberFormat(locale, { style: 'currency', currency: currency }).format(n); }
        catch (e2) { /* fall through */ }
      }
      return n.toFixed(2);
    };

    /* suggest.json returns `tags` as an array of strings; this tolerates a bare
       string too so a payload shape change degrades to "show the price" rather
       than throwing inside render(). */
    var hasTag = function (p, tag) {
      var t = p && p.tags;
      if (!t) return false;
      if (typeof t === 'string') return t === tag;
      return t.indexOf(tag) !== -1;
    };

    var open = function () {
      closeNavPanel();
      overlay.hidden = false;
      overlay.classList.add('is-open');
      opener.setAttribute('aria-expanded', 'true');
      if (input) { input.focus(); input.select(); }
    };

    var close = function () {
      overlay.classList.remove('is-open');
      overlay.hidden = true;
      opener.setAttribute('aria-expanded', 'false');
      if (results) results.innerHTML = '';
    };

    opener.addEventListener('click', function (e) {
      e.preventDefault();
      if (overlay.hidden) { open(); } else { close(); }
    });

    if (closeBtn) closeBtn.addEventListener('click', close);

    doc.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !overlay.hidden) { close(); opener.focus(); }
    });

    doc.addEventListener('click', function (e) {
      if (overlay.hidden) return;
      if (overlay.contains(e.target) || opener.contains(e.target)) return;
      close();
    });

    /* Enter = native GET to /search, so the form is left alone on submit. */
    if (form) form.addEventListener('submit', function () { if (debounce) clearTimeout(debounce); });

    var render = function (products, q) {
      if (!results) return;
      if (!products.length) {
        results.innerHTML = '<p class="sx-state">No results for &ldquo;' + esc(q) + '&rdquo;</p>';
        return;
      }
      var html = '<div class="sx-grid">';
      products.forEach(function (p) {
        /* 2026-08-09: The Pen is never listed as a product on any surface
           (CLAUDE.md § THE PEN — NEVER SOLD) — the one suggest.json result
           the Liquid-side skips can't reach. Handle first, URL fallback in
           case the fields list ever drops `handle`. */
        if (p.handle === 'the-pen' || (p.url && p.url.indexOf('/products/the-pen') !== -1)) return;
        var img = p.featured_image && p.featured_image.url ? p.featured_image.url : p.image;
        /* 2026-08-06 fix: predictive results were the ONE surface still printing
           a price for unlaunched and display-only SKUs -- $60 for The Notebook
           and $15 for The Pen, neither of which can be bought, and $79 for The
           Lived-In Ledger, which is never sold. Same recipe as the nav panels,
           the collection grid and the /search template:
             coming-soon  -> badge, NO price
             display-only -> "Free with The Ledger" in place of the price
           `tags` is already in the suggest.json payload with the current
           `fields` list (verified 2026-08-06). Do NOT add `tag` to
           resources[options][fields] to "guarantee" it: that param controls
           what is SEARCHED, not what is returned, so it would make internal tag
           vocabulary matchable -- typing "soon" would start returning The Pen
           and The Notebook (verified against the live endpoint). */
        var soon = hasTag(p, 'coming-soon');
        var displayOnly = hasTag(p, 'display-only');
        var priceRow;
        if (soon) {
          priceRow = ''; /* no price on an unlaunched SKU */
        } else if (displayOnly) {
          /* The nav-panel note pair verbatim. .nav-card__price is byte-identical
             to .sx-card__price (13.5px / 600 / tabular / ink) and --note adds
             the brick-red 500 treatment; pairing --note with .sx-card__price
             instead would LOSE, because .sx-card__price is declared later in
             components.css at equal specificity (verified in-browser: the note
             rendered ink 600). Both classes live in components.css, which loads
             on every route -- store.css does not, and this overlay is
             site-wide. */
          priceRow = '<span class="nav-card__price nav-card__price--note">Free with The Ledger</span>';
        } else {
          priceRow = '<span class="sx-card__price">' + esc(money(p.price)) + '</span>';
        }
        html += '<a class="sx-card" href="' + esc(p.url) + '">'
          /* .sx-card is position:relative, so the canonical badge floats on the
             card corner exactly as it does on .nav-card / .sx-pcard. */
          + (soon ? '<span class="nav-card__badge">Coming soon</span>' : '')
          + '<span class="sx-card__media">'
          + (img ? '<img src="' + esc(img) + '&width=400" alt="' + esc(p.title) + '" width="200" height="200" loading="lazy">' : '<span class="sx-card__ph" aria-hidden="true"></span>')
          + '</span>'
          + '<span class="sx-card__name">' + esc(p.title) + '</span>'
          + priceRow
          + '</a>';
      });
      html += '</div>';
      results.innerHTML = html;
    };

    var run = function (q) {
      var id = ++seq;
      /* Polish pass 2026-08-06: the pending state was a bare label, easy to
         miss between two 250ms debounces on a fast connection. .sx-spin is a
         CSS-only 14px ring (components.css), removed under reduced motion. */
      if (results) results.innerHTML = '<p class="sx-state"><span class="sx-spin" aria-hidden="true"></span>Searching&hellip;</p>';
      var url = base
        + '?q=' + encodeURIComponent(q)
        + '&resources[type]=product'
        + '&resources[limit]=8'
        + '&resources[options][unavailable_products]=last'
        + '&resources[options][fields]=title,product_type,variants.title';
      fetch(url, { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (data) {
          if (id !== seq || overlay.hidden) return;
          var list = (data && data.resources && data.resources.results && data.resources.results.products) || [];
          /* $0 utility products (Digital Bonus Pack / Unlimited Future Access /
             Set Exclusives) are cart plumbing, not catalogue -- same rule the
             /search template applies. `display-only` is the escape hatch. */
          list = list.filter(function (p) {
            var price = parseFloat(p.price);
            if (!isNaN(price) && price === 0) {
              return !!(p.tags && p.tags.indexOf('display-only') !== -1);
            }
            return true;
          });
          render(list, q);
        })
        .catch(function () {
          if (id !== seq || overlay.hidden) return;
          if (results) results.innerHTML = '<p class="sx-state">Search is unavailable right now. Press Enter to search the store.</p>';
        });
    };

    if (input) {
      input.addEventListener('input', function () {
        var q = input.value.trim();
        if (debounce) clearTimeout(debounce);
        if (q.length < 2) {
          seq++;
          if (results) results.innerHTML = '';
          return;
        }
        debounce = setTimeout(function () { run(q); }, 250);
      });
    }
  }());

  /* ----- C. country selector ----------------------------------------------- */

  (function countrySelect() {
    var A = 127397; /* 'A'.codePointAt(0) offset to REGIONAL INDICATOR SYMBOL LETTER A */
    var flag = function (iso) {
      if (!iso || iso.length !== 2 || !String.fromCodePoint) return '';
      try {
        return String.fromCodePoint(iso.toUpperCase().charCodeAt(0) + A, iso.toUpperCase().charCodeAt(1) + A);
      } catch (e) { return ''; }
    };

    toArray(doc.querySelectorAll('[data-country-select]')).forEach(function (root) {
      if (root.getAttribute('data-sx-init') === '1') return;
      var form = root.querySelector('form');
      var native = root.querySelector('[data-country-native]');
      var trigger = root.querySelector('[data-country-trigger]');
      var panel = root.querySelector('[data-country-panel]');
      var search = root.querySelector('[data-country-search]');
      var empty = root.querySelector('[data-country-empty]');
      var rows = toArray(root.querySelectorAll('[data-country-row]'));
      if (!form || !native || !trigger || !panel) return;
      root.setAttribute('data-sx-init', '1');

      /* 2026-11-03 (QA: a persona page, e.g. ?view=gift, must survive a country change). Shopify's own return_to holds the URL
         as it was RENDERED; this rewrites it from the CURRENT address on submit, so ?view= and anything the page has written
         since (tier, colours) come back after the switch. Dropped: Shopify's search/recommendation tracking params, the
         country/currency/locale params (they would fight the new choice) and our one-shot cart-restore params. Everything
         else stays, including preview_theme_id (see the note in snippets/country-selector.liquid). Any error = the
         built-in value is left untouched, so this can only ever add query, never break the switch. */
      var DROP = ['_pos', '_sid', '_ss', '_psq', '_v', '_fid', 'country', 'currency', 'locale', 'restore', 'cart'];
      function syncReturnTo() {
        try {
          var rt = form.querySelector('input[name="return_to"]'); if (!rt) return;
          var q = new URLSearchParams(window.location.search);
          DROP.forEach(function (k) { q.delete(k); });
          var qs = q.toString();
          var next = window.location.pathname + (qs ? '?' + qs : '');
          if (next.charAt(0) === '/' && next.charAt(1) !== '/') rt.value = next;
        } catch (e) { /* keep Shopify's own return_to */ }
      }
      form.addEventListener('submit', syncReturnTo);

      /* swap the no-JS native select for the custom UI */
      root.classList.add('country-select--js');
      trigger.hidden = false;

      toArray(root.querySelectorAll('.country-select__flag[data-iso]')).forEach(function (el) {
        var f = flag(el.getAttribute('data-iso'));
        if (f) el.textContent = f;
      });

      var close = function () {
        panel.hidden = true;
        trigger.setAttribute('aria-expanded', 'false');
      };

      var open = function () {
        panel.hidden = false;
        trigger.setAttribute('aria-expanded', 'true');
        if (search) { search.value = ''; search.focus(); }
        rows.forEach(function (r) { r.hidden = false; });
        if (empty) empty.hidden = true;
        var cur = panel.querySelector('.country-select__opt.is-current');
        if (cur && cur.scrollIntoView) {
          try { cur.scrollIntoView({ block: 'nearest' }); } catch (e) { /* older Safari */ }
        }
      };

      trigger.addEventListener('click', function (e) {
        e.preventDefault();
        if (panel.hidden) { open(); } else { close(); }
      });

      if (search) {
        search.addEventListener('input', function () {
          var q = search.value.trim().toLowerCase();
          var shown = 0;
          rows.forEach(function (r) {
            var hit = !q || (r.getAttribute('data-name') || '').indexOf(q) !== -1;
            r.hidden = !hit;
            if (hit) shown++;
          });
          if (empty) empty.hidden = shown !== 0;
        });
      }

      panel.addEventListener('click', function (e) {
        var btn = e.target && e.target.closest ? e.target.closest('[data-country-opt]') : null;
        if (!btn) return;
        e.preventDefault();
        /* Polish pass 2026-08-06: choosing a market submits the localization
           form and the page reloads, which is one to two seconds of nothing on
           a slow connection -- long enough to tap a second country. .is-busy
           dims and disables the widget (components.css) until navigation
           takes over. It is never cleared on purpose: the only way out of this
           state is the reload, so a stalled request cannot leave the widget
           live with a half-applied choice. */
        root.classList.add('is-busy');
        native.value = btn.getAttribute('data-country-opt');
        syncReturnTo(); /* form.submit() (old browsers) skips the submit event, so sync here too */
        if (typeof form.requestSubmit === 'function') { form.requestSubmit(); } else { form.submit(); }
      });

      /* Scoped to the widget, NOT document: inside the mobile-nav overlay the
         theme's own document-level Escape handler closes the whole menu, and
         it is registered first so it always wins. Listening on the root and
         stopping propagation means Escape dismisses the country dropdown only
         and the overlay behind it stays open. Verified 2026-08-06 at 390px:
         before this, one Escape shut the menu and the next tap on the close
         button re-opened it. */
      root.addEventListener('keydown', function (e) {
        if (e.key !== 'Escape' || panel.hidden) return;
        e.stopPropagation();
        close();
        trigger.focus();
      });

      doc.addEventListener('click', function (e) {
        if (panel.hidden || root.contains(e.target)) return;
        close();
      });
    });
  }());

  /* ----- D. mobile-nav accordions ------------------------------------------ */

  /* EXCLUSIVE-OPEN (2026-08-27, T14, founder ruling): opening one group closes
     whichever group was open, so the overlay never grows past one expanded
     section. aria-expanded is written on BOTH sides of the swap -- the one
     being opened and the one being auto-closed -- and each panel's `hidden`
     follows its own button, so the attribute state can never disagree with
     what is on screen.

     The entry animation is untouched: .mobile-nav__panel:not([hidden]) runs
     sx-acc-in, and because the auto-closed panel goes to display:none in the
     same tick the newly opened one is the only thing that animates -- the same
     single-panel motion the old one-at-a-time-by-hand behaviour produced.

     `accs` is captured once at init. The markup is static Liquid, and the
     per-button data-sx-init guard still makes a re-run idempotent. The Ledger
     row is deliberately absent from this list: it is an <a> now, not a
     [data-mnav-acc] button (snippets/mobile-nav.liquid). */
  (function mobileAccordions() {
    var accs = toArray(doc.querySelectorAll('[data-mnav-acc]'));

    var shut = function (btn) {
      if (btn.getAttribute('aria-expanded') !== 'true') return;
      btn.setAttribute('aria-expanded', 'false');
      var p = doc.getElementById(btn.getAttribute('aria-controls') || '');
      if (p) p.hidden = true;
    };

    accs.forEach(function (btn) {
      if (btn.getAttribute('data-sx-init') === '1') return;
      btn.setAttribute('data-sx-init', '1');
      var panel = doc.getElementById(btn.getAttribute('aria-controls') || '');
      btn.addEventListener('click', function () {
        var isOpen = btn.getAttribute('aria-expanded') === 'true';
        if (!isOpen) {
          accs.forEach(function (other) { if (other !== btn) shut(other); });
        }
        btn.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
        if (panel) panel.hidden = isOpen;
      });
    });
  }());

  /* ----- E. quick-add on product cards ------------------------------------
     Batch 3 / Phase B (2026-08-08). Teardown section 2.9.

     WHAT IS COPIED: the in-place button morph. `+` -> a widening pill that
     says it is working -> a confirmed state, then back. One control, three
     classes, a min-width transition. It beats a toast because the feedback
     lands on the thing the finger just touched, and it needs no new surface.

     WHAT IS DELIBERATELY NOT COPIED: the reference stamps a hard-coded
     data-variant-id on EVERY collection card, including multi-variant
     products -- clicking `+` on their 3-size desk pad added size Small with no
     picker and no PDP redirect. Their own homepage rail does the right thing
     (`+` only for single-variant SKUs, a link to the PDP otherwise) and that
     is the half of their system implemented here. The Ledger has five leather
     colourways at $100; silently choosing one is a returns generator. The
     eligibility test lives in snippets/product-card.liquid, so a button that
     reaches this handler is single-variant, in stock and purchasable by
     construction.

     Delegated on the document, so it is idempotent by construction and
     survives any DOM the cart drawer swaps in. The hooks are generic
     (`[data-quick-add]` + data-variant-id + data-product-title) because the
     homepage phase reuses this component.

     No-JS / no-cart path: the card's title link is a real anchor to the PDP,
     so the card still functions with this script absent. If cart.js failed to
     load but this did, the button navigates to the PDP rather than dying
     silently. ----------------------------------------------------------- */

  (function quickAdd() {
    var REVERT_MS = 1800;
    if (doc.documentElement.getAttribute('data-sx-qadd') === '1') return;
    doc.documentElement.setAttribute('data-sx-qadd', '1');

    doc.addEventListener('click', function (e) {
      var t = e.target;
      var btn = t && t.closest ? t.closest('[data-quick-add]') : null;
      if (!btn) return;

      if (btn.disabled) { e.preventDefault(); return; }
      var id = parseInt(btn.getAttribute('data-variant-id'), 10);
      if (!id) { e.preventDefault(); return; }

      var api = window.OrdinCart;
      if (!api || typeof api.add !== 'function') {
        /* cart.js missing. Card buttons carry data-product-url and get sent
           to the PDP. The digital-PDP bar (Batch 3 / Phase D) carries NO url
           and sits inside a real {% form 'product' %} as type="submit", so
           NOT preventing default here lets the native POST to /cart/add
           proceed -- the same no-cart fallback the old bespoke enhancer had. */
        var url = btn.getAttribute('data-product-url');
        if (url) { e.preventDefault(); window.location.href = url; }
        return;
      }
      e.preventDefault();
      /* cart.js serialises every mutation on one queue; refusing while it is
         busy keeps the button's own state honest rather than queueing a
         second morph behind the first. */
      if (api.isBusy && api.isBusy()) return;

      var title = btn.getAttribute('data-product-title') || 'item';
      var label = btn.querySelector('[data-quick-add-label]');
      if (btn.sxTimer) { clearTimeout(btn.sxTimer); btn.sxTimer = null; }

      var reset = function () {
        btn.classList.remove('is-added');
        btn.classList.remove('is-loading');
        if (label) label.textContent = '';
        btn.setAttribute('aria-label', 'Add ' + title + ' to cart');
      };

      btn.disabled = true;
      btn.classList.remove('is-added');
      btn.classList.add('is-loading');
      if (label) label.textContent = 'Adding';

      api.add([{ id: id, quantity: 1 }]).then(function () {
        btn.classList.remove('is-loading');
        btn.classList.add('is-added');
        if (label) label.textContent = 'Added';
        /* The label is aria-hidden (it is decorative duplication of the
           control's own name), so the state change is announced by renaming
           the button itself. */
        btn.setAttribute('aria-label', 'Added ' + title + ' to cart');
        if (typeof api.open === 'function') api.open();
      }).catch(function () {
        reset();
      }).then(function () {
        btn.disabled = false;
        btn.sxTimer = setTimeout(reset, REVERT_MS);
      });
    });
  }());

  /* ----- F. collection category chips -------------------------------------
     Teardown sections 2.3/2.4. Instant client-side filtering over the
     server-rendered grid: every card stays in the DOM and non-matching ones
     get `.is-hidden`, matched against the `data-cats` list Liquid bakes onto
     each card. No round trip, no re-render flash, scroll position untouched.

     THE ONE FIX ON TOP OF THE REFERENCE: they hold this state nowhere. Their
     `location.href` never changes, `?filter=` in the URL is ignored, the back
     button does not undo a filter, and analytics sees one pageview for every
     filter state. Ordin runs PostHog segmentation on real URLs, so the active
     chip is written to `?filter=` with history.replaceState (replace, not
     push: a filter is a view of one page, not a navigation, and pushState
     would make Back walk backwards through chip clicks) and honoured on load.
     The ON-LOAD half runs inline inside sections/main-collection.liquid so it
     lands before first paint -- this file is deferred and would flash the
     unfiltered grid first. This module owns every interaction after that and
     simply reads the state it finds.

     SCOPE LIMIT: filtering acts on the LOADED page (24 per page). At 7
     products that is the whole catalogue. Past 24 the fix is to point the
     chips at the real collection URLs, not to paginate the client filter. ---- */

  (function categoryChips() {
    var chips = doc.querySelector('[data-filter-chips]');
    var grid = doc.querySelector('[data-filter-grid]');
    if (!chips || !grid) return;
    if (chips.getAttribute('data-sx-init') === '1') return;
    chips.setAttribute('data-sx-init', '1');

    var count = doc.querySelector('[data-filter-count]');
    var empty = doc.querySelector('[data-filter-empty]');
    var sortFilter = doc.querySelector('[data-sort-filter]');
    var cards = toArray(grid.children);

    var apply = function (f) {
      var n = 0;
      cards.forEach(function (li) {
        var hit = f === 'all' ||
          (' ' + (li.getAttribute('data-cats') || '') + ' ').indexOf(' ' + f + ' ') !== -1;
        li.classList.toggle('is-hidden', !hit);
        if (hit) n++;
      });
      toArray(chips.children).forEach(function (c) {
        var on = c.getAttribute('data-filter') === f;
        c.classList.toggle('is-active', on);
        c.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      if (count) count.textContent = n + (n === 1 ? ' item' : ' items');
      if (empty) empty.hidden = n !== 0;
      /* Keep the sort form's hidden field in step, so sorting a filtered view
         comes back to the same filtered view after the reload. */
      if (sortFilter) sortFilter.value = f;

      try {
        var url = new URL(window.location.href);
        if (f === 'all') { url.searchParams.delete('filter'); }
        else { url.searchParams.set('filter', f); }
        window.history.replaceState(null, '', url.toString());
      } catch (err) { /* no URL/history: filtering still works, just unshareable */ }
    };

    chips.addEventListener('click', function (e) {
      var t = e.target;
      var btn = t && t.closest ? t.closest('[data-filter]') : null;
      if (!btn || !chips.contains(btn)) return;
      apply(btn.getAttribute('data-filter'));
    });
  }());

  /* ----- G. sort <select> auto-submit --------------------------------------
     Progressive enhancement over a real GET form: without this the <noscript>
     Apply button submits it, and either way the destination is Shopify's own
     `?sort_by` URL. The reference store reorders the DOM in JS and ignores
     `?sort_by` entirely, which costs it shareable and crawlable sorted views;
     at a 7-SKU catalogue the reload is imperceptible and the URL stays true.
     ---------------------------------------------------------------------- */

  (function sortSelects() {
    toArray(doc.querySelectorAll('[data-sort-select]')).forEach(function (sel) {
      if (sel.getAttribute('data-sx-init') === '1') return;
      sel.setAttribute('data-sx-init', '1');
      sel.addEventListener('change', function () {
        var form = sel.form;
        if (!form) return;
        /* Don't serialise `filter=all` -- the default state belongs in a bare
           URL, not in a parameter that says "no filter". */
        var hidden = form.querySelector('[data-sort-filter]');
        if (hidden) hidden.disabled = !hidden.value || hidden.value === 'all';
        if (typeof form.requestSubmit === 'function') { form.requestSubmit(); }
        else { form.submit(); }
      });
    });
  }());

  /* ----- H. /pages/bundles prose disclosure (MOBILE ONLY) -------------------
     2026-08-27 (T14). Founder: the three-tier comparison reads text-heavy at
     390px. No copy was rewritten -- below 760px each tier card's SELL PROSE
     (.sx-bundle__line + the pen line + the second-Notebook box) collapses
     behind its own control so what remains is the part that actually compares:
     tier name, price and the contents manifest, three cards in far less scroll.

     PROGRESSIVE, NOT CSS-GATED. The server renders the prose VISIBLE and the
     button `hidden` (snippets/bundle-prose-toggle.liquid), so a phone with JS
     off gets the page exactly as it shipped on 2026-08-19 -- nothing is hidden
     behind a control that cannot be operated. A CSS-only collapse would have
     stranded that visitor.

     DESKTOP IS UNTOUCHED BY CONSTRUCTION: at >=760px sync() force-opens every
     prose block and re-hides every button, so no state a user reached on a
     phone can survive a rotate/resize into the desktop layout. 760px is the
     same breakpoint .sx-bundle--ladder switches on (assets/store.css).

     Per-card state lives in data-sx-state on the button, so opening one tier's
     prose does NOT close another's -- unlike the mobile-nav accordions (unit
     D), these three are a comparison and the reader may well want two open.
     ---------------------------------------------------------------------- */

  (function bundleProse() {
    var toggles = toArray(doc.querySelectorAll('[data-sx-prose-toggle]'));
    if (!toggles.length) return;

    /* Held as a MediaQueryList rather than re-querying, so the breakpoint can
       be listened to directly (below) as well as read. Falls back to the mq()
       helper if matchMedia is missing -- then isNarrow() is permanently false,
       which yields the desktop shape: prose open, button hidden. That is the
       correct degradation: never hide copy from a browser we cannot measure. */
    var mql = (window.matchMedia && window.matchMedia('(min-width: 760px)')) || null;
    var isNarrow = function () {
      return !(mql ? mql.matches : mq('(min-width: 760px)'));
    };

    var sync = function () {
      var narrow = isNarrow();
      toggles.forEach(function (btn) {
        var panel = doc.getElementById(btn.getAttribute('aria-controls') || '');
        if (!panel) return;
        if (!narrow) {
          btn.hidden = true;
          panel.hidden = false;
          btn.setAttribute('aria-expanded', 'true');
          return;
        }
        btn.hidden = false;
        var open = btn.getAttribute('data-sx-state') === 'open';
        panel.hidden = !open;
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    };

    toggles.forEach(function (btn) {
      if (btn.getAttribute('data-sx-init') === '1') return;
      btn.setAttribute('data-sx-init', '1');
      btn.setAttribute('data-sx-state', 'closed');
      btn.addEventListener('click', function () {
        btn.setAttribute('data-sx-state',
          btn.getAttribute('aria-expanded') === 'true' ? 'closed' : 'open');
        sync();
      });
    });

    sync();

    /* Listen on the BREAKPOINT, not on every resize frame: sync() only ever
       changes anything when the 760px line is crossed. `resize` is kept as the
       fallback for Safari < 14, which has MediaQueryList but not its
       addEventListener. Both are harmless if they fire together -- sync() is
       idempotent. (Note for future QA: Chrome DevTools/CDP viewport emulation
       does NOT dispatch `resize`, so the mql path is also what makes this
       testable in a headless harness.) */
    if (mql && typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', sync);
    } else {
      window.addEventListener('resize', sync, { passive: true });
    }
  }());
})();
/* ===== END STORE-EXPANSION HEADER BAND ===== */

/* 2026-10-15 (founder): button shine on press + spinner while loading (CSS: .btn-fx in
   assets/components.css). Delegated, so buttons re-rendered by the cart drawer swap keep it.
   The checkout link and the drawer's Add buttons spin until the page or drawer replaces them;
   the PDP Add to Cart spins through its own aria-busy (assets/pdp.js setAtcLoading). */
(function () {
  function shine(btn) {
    btn.classList.remove('is-shine');
    void btn.offsetWidth;
    btn.classList.add('is-shine');
    window.setTimeout(function () { btn.classList.remove('is-shine'); }, 700);
  }
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!(t instanceof Element)) return;
    var btn = t.closest('.btn-fx');
    if (!btn || btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;
    shine(btn);
    if (btn.hasAttribute('data-btn-load') || btn.hasAttribute('data-upsell-add')) {
      btn.classList.add('is-loading');
      window.setTimeout(function () { btn.classList.remove('is-loading'); }, 8000);
    }
  }, true);
  window.addEventListener('pageshow', function () {
    document.querySelectorAll('.btn-fx.is-loading').forEach(function (b) { b.classList.remove('is-loading'); });
  });
})();
